import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { FaUpload, FaEdit, FaDatabase, FaCodeBranch, FaRobot, FaCheck } from "react-icons/fa";
import axios from "../../utils/axios";
import { message, Button, Select } from "antd";

const { Option } = Select;

const steps = [
  { id: 1, title: "Upload Images", icon: <FaUpload />, description: "Import your dataset" },
  { id: 2, title: "Annotate Images", icon: <FaEdit />, description: "Label your data" },
  { id: 3, title: "Dataset", icon: <FaDatabase />, description: "Prepare training data" },
  { id: 4, title: "Create Versions", icon: <FaCodeBranch />, description: "Version control" },
  { id: 5, title: "Train Model", icon: <FaRobot />, description: "AI model training" },
];

const Workflow = () => {
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem("user"));
  const projectname =
    location.state?.projectname ||
    localStorage.getItem("projectname") ||        
    "";
  const organizationId = localStorage.getItem("organizationId");
  const [projects, setProjects] = useState([]);
  const [versions, setVersions] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [version, setVersion] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [workflowStatus, setWorkflowStatus] = useState({
    uploaded: false,
    annotated: false,
    dataset: false,
    versioned: false,
    trained: false,
  });
  const [workflow, setWorkflow] = useState(false)

  const getProjects = async () => {
    try {
      setLoading(true);
      const res = await axios.post("get-all-project-names", { username: user.username, projectname, organizationId });
      if (res?.data?.projectNames?.length) {
        setProjects(res.data.projectNames);
      }
    } catch (err) {
      if (err.response?.data?.msg) {
        message.warning(err.response.data.msg);
      } else if (err.response?.status === 401) {
        message.error("User not authorised");
      } else {
        message.error("Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProjects();
  }, []);

  const getWorkflow = async () => {
    if (!selectedProject || !version) return;

    try {
      setLoading(true);
      const res = await axios.post("get-project-workflow", {
        username: user.username,
        projectname: selectedProject,
        version,
      });

      if (res?.data?.status) {
        const { uploaded, annotated, dataset, versioned, trained } = res.data.status;

        setWorkflowStatus({ uploaded, annotated, dataset, versioned, trained });
        setWorkflow(true);
        // Determine currentStep based on status
        let step = 0;
        if (trained) step = 5;
        else if (versioned) step = 4;
        else if (dataset) step = 3;
        else if (annotated) step = 2;
        else if (uploaded) step = 1;

        setCurrentStep(step);
      }
    } catch (err) {
      message.error(err.response?.data?.msg || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // Mock API for versions (replace with actual API call)
  useEffect(() => {
    if (selectedProject) {
      // Fetch versions from backend based on selected project
      setVersions(["v1", "v2", "v3"]);
    } else {
      setVersions([]);
      setVersion(null);
    }
  }, [selectedProject]);

  const getStepStatus = (stepId) => {
    switch (stepId) {
      case 1: return workflowStatus.uploaded;
      case 2: return workflowStatus.annotated;
      case 3: return workflowStatus.dataset;
      case 4: return workflowStatus.versioned;
      case 5: return workflowStatus.trained;
      default: return false;
    }
  };

  const getNextIncompleteStep = () => {
    for (let i = 1; i <= 5; i++) {
      if (!getStepStatus(i)) return i;
    }
    return 5; // All complete
  };

  const completedSteps = steps.filter(step => getStepStatus(step.id)).length;
  const progressPercentage = (completedSteps / steps.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="container mx-auto px-4 py-5 max-w-7xl">
        {/* Header Section */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
              AI Workflow Dashboard
            </h1>
            {/* <p className="text-gray-600 text-lg">Manage your machine learning pipeline with ease</p> */}
          </motion.div>
        </div>

        {/* Control Panel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/80 backdrop-blur-sm border border-white/20 shadow-xl rounded-3xl p-6 mb-12"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-end">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Project
              </label>
              <Select
                placeholder="Choose your project"
                value={selectedProject}
                onChange={setSelectedProject}
                loading={loading}
                className="w-full"
                size="large"
                allowClear
                style={{
                  borderRadius: '12px',
                }}
              >
                {projects.map((p) => (
                  <Option key={p.projectname} value={p.projectname}>
                    {p.projectname}
                  </Option>
                ))}
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Version
              </label>
              <Select
                placeholder="Select version"
                value={version}
                onChange={(val) => setVersion(val)}
                className="w-full"
                size="large"
                allowClear
                disabled={!selectedProject}
                style={{
                  borderRadius: '12px',
                }}
              >
                {versions.map((v) => (
                  <Option key={v} value={v}>
                    {v}
                  </Option>
                ))}
              </Select>
            </div>

            <Button
              type="primary"
              size="large"
              disabled={!selectedProject || !version}
              onClick={getWorkflow}
              loading={loading}
              className="h-12 px-8 bg-gradient-to-r from-blue-500 to-purple-500 border-0 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Load Workflow
            </Button>
          </div>
        </motion.div>

        {/* Workflow Visualization */}
        {workflow ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="space-y-8"
          >
            {/* Project Info & Progress */}
            <div className="bg-white/80 backdrop-blur-sm border border-white/20 shadow-xl rounded-3xl p-6">
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">
                    {selectedProject} <span className="text-blue-500">- {version}</span>
                  </h2>
                  <p className="text-gray-600">
                    {completedSteps} of {steps.length} steps completed
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-2xl font-bold text-blue-600">{Math.round(progressPercentage)}%</div>
                    <div className="text-sm text-gray-500">Progress</div>
                  </div>
                  <div className="w-16 h-16 relative">
                    <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                      <path
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke="#e5e7eb"
                        strokeWidth="3"
                      />
                      <path
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke="url(#gradient)"
                        strokeWidth="3"
                        strokeDasharray={`${progressPercentage}, 100`}
                        className="transition-all duration-500"
                      />
                      <defs>
                        <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#3b82f6" />
                          <stop offset="100%" stopColor="#8b5cf6" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-2 mb-8">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercentage}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                  className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full"
                />
              </div>
            </div>

            {/* Steps Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {steps.map((step, index) => {
                const isCompleted = getStepStatus(step.id);
                const isNext = getNextIncompleteStep() === step.id;
                const isActive = currentStep === step.id;

                return (
                  <motion.div
                    key={step.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    onClick={() => setCurrentStep(step.id)}
                    className={`relative cursor-pointer group transition-all duration-300 ${
                      isActive ? 'scale-105' : 'hover:scale-102'
                    }`}
                  >
                    <div
                      className={`
                        relative p-6 rounded-2xl border-2 transition-all duration-300
                        ${isCompleted 
                          ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-green-100' 
                          : isNext 
                          ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 shadow-blue-100'
                          : 'bg-white border-gray-200 shadow-gray-100'
                        }
                        shadow-lg group-hover:shadow-xl
                      `}
                    >
                      {/* Step Number & Icon */}
                      <div className="flex items-center justify-between mb-4">
                        <div
                          className={`
                            w-12 h-12 rounded-xl flex items-center justify-center text-lg font-semibold transition-all duration-300
                            ${isCompleted 
                              ? 'bg-green-500 text-white shadow-lg shadow-green-200' 
                              : isNext
                              ? 'bg-blue-500 text-white shadow-lg shadow-blue-200'
                              : 'bg-gray-100 text-gray-500'
                            }
                          `}
                        >
                          {isCompleted ? <FaCheck /> : step.icon}
                        </div>
                        
                        {/* Status Badge */}
                        <div
                          className={`
                            px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide
                            ${isCompleted 
                              ? 'bg-green-100 text-green-700' 
                              : isNext
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-500'
                            }
                          `}
                        >
                          {isCompleted ? 'Done' : isNext ? 'Next' : 'Pending'}
                        </div>
                      </div>

                      {/* Step Info */}
                      <div>
                        <h3 className="font-bold text-gray-800 mb-2 text-lg">
                          {step.title}
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {step.description}
                        </p>
                      </div>

                      {/* Completion Indicator */}
                      {isCompleted && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-lg"
                        >
                          <FaCheck className="text-white text-xs" />
                        </motion.div>
                      )}

                      {/* Active Pulse */}
                      {isNext && !isCompleted && (
                        <motion.div
                          animate={{ 
                            boxShadow: [
                              '0 0 0 0 rgba(59, 130, 246, 0.7)',
                              '0 0 0 10px rgba(59, 130, 246, 0)',
                            ],
                          }}
                          transition={{
                            duration: 1.5,
                            repeat: Infinity,
                          }}
                          className="absolute inset-0 rounded-2xl"
                        />
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Next Action CTA */}
            {/* {completedSteps < steps.length && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="text-center"
              >
                <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl p-8 text-white">
                  <h3 className="text-xl font-bold mb-2">
                    Next: {steps[getNextIncompleteStep() - 1]?.title}
                  </h3>
                  <p className="opacity-90 mb-4">
                    Continue with {steps[getNextIncompleteStep() - 1]?.description.toLowerCase()}
                  </p>
                  <Button
                    size="large"
                    className="bg-white text-blue-600 border-0 rounded-xl font-semibold px-8 hover:bg-gray-50"
                  >
                    Continue Workflow
                  </Button>
                </div>
              </motion.div>
            )} */}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-10"
          >
            <div className="bg-white/80 backdrop-blur-sm border border-white/20 shadow-xl rounded-3xl p-12 max-w-md mx-auto">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaRobot className="text-3xl text-blue-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">
                Select Project & Version
              </h3>
              <p className="text-gray-600">
                Choose a project and version to view your workflow status
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Workflow;